import { useState, useCallback, useEffect } from 'react';
import { Pagination, Response, SelectItemProps, usePrevious } from '@ui/src';
import { useAppSelector } from '@store';
import { PspAccount } from '../types';
import { useLazyGetProcessingRulePspParnterAccountsQuery } from '../store/service';

interface ListResponse {
  data: Response<PspAccount>;
}

let timeout: NodeJS.Timeout;

const useGetPrimaryAccountsAutocompleteList = (
  id?: string,
  currencyId?: string[],
  ruleId?: string,
) => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [autocompleteListPagination, setAutocompleteListPagination] =
    useState<Pagination>();
  const [initialList, setInitialList] = useState<SelectItemProps[]>([]);
  const [accountsList, setAccountsList] = useState<PspAccount[]>([]);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);

  const { overdraftEnabled } = useAppSelector(
    (state) => state.transactionsDictionaries,
  );

  const prevId = usePrevious(id);

  const [getList, { isFetching, isLoading }] =
    useLazyGetProcessingRulePspParnterAccountsQuery();

  const getAutocompleteListAction = useCallback(
    async (pspPartnerId?: string, currenciesIdsList?: string[]) => {
      const results: SelectItemProps[] = [];
      const accounts: PspAccount[] = [];

      const partnerId = pspPartnerId || id;
      const currenciesList = currenciesIdsList || currencyId;

      if (partnerId) {
        const response = (await getList({
          id: partnerId,
          query: `?take=20${
            currenciesList && currenciesList?.length
              ? `&currencyId=${currenciesList?.join('&currencyId=')}`
              : ''
          }`,
        })) as ListResponse;

        if (response?.data?.data) {
          response.data.data.edges?.forEach((item) => {
            if (!results.find((listItem) => listItem.value === item.node.id)) {
              results.push({
                name: item.node.description || '',
                description: '',
                value: item.node?.id,
                suffix: `${item.node.account?.fullNumber} ${
                  // eslint-disable-next-line no-nested-ternary
                  overdraftEnabled
                    ? item?.node?.account?.overdraftEnabled
                      ? '(Overdraft)'
                      : ''
                    : ''
                }`,
              });
            }

            if (!accounts.find((account) => account.id === item.node.id)) {
              accounts.push(item.node);
            }
          });

          setAutocompleteListPagination(response?.data?.data?.pageInfo);
          setNextPageIsLoading(false);
        }
      }
      setInitialList(results);
      setAutocompleteList(results);
      setAccountsList(accounts);
    },
    [id, currencyId],
  );

  const onScrollAutocompleteListAction = async (cursor: string) => {
    const results = ([] as SelectItemProps[]).concat(autocompleteList);
    const accounts = ([] as PspAccount[]).concat(accountsList);

    if (id) {
      const response = (await getList({
        id,
        query: `?take=20${
          currencyId && currencyId?.length
            ? `&currencyId=${currencyId?.join('&currencyId=')}`
            : ''
        }&cursor=${cursor}`,
      })) as ListResponse;

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          if (!results.find((listItem) => listItem.value === item.node.id)) {
            results.push({
              name: item.node.description || '',
              description: '',
              value: item.node?.id,
              suffix: `${item.node.account?.fullNumber} ${
                // eslint-disable-next-line no-nested-ternary
                overdraftEnabled
                  ? item?.node?.account?.overdraftEnabled
                    ? '(Overdraft)'
                    : ''
                  : ''
              }`,
            });
          }

          if (!accounts.find((account) => account.id === item.node.id)) {
            accounts.push(item.node);
          }
        });

        setAutocompleteListPagination(response?.data?.data?.pageInfo);
        setNextPageIsLoading(false);
      }
    }
    setInitialList(results);
    setAutocompleteList(results);
    setAccountsList(accounts);
  };

  const onSearch = (search: string) => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      setAutocompleteList(
        initialList.filter(
          (item) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.suffix?.toLowerCase().includes(search.toLowerCase()) ||
            item.description?.toLowerCase().includes(search.toLowerCase()),
        ),
      );
    }, 300);
  };

  useEffect(() => {
    if (
      !ruleId ||
      (prevId !== null && prevId !== undefined && id !== prevId && ruleId)
    ) {
      getAutocompleteListAction();
    }
  }, [getAutocompleteListAction, ruleId, prevId, id]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    onSearch,
    isLoading: isLoading || isFetching,
    accountsList,
    setAutocompleteList,
    setAccountsList,
    setInitialList,
    autocompleteListPagination,
    nextPageIsLoading,
    onScrollAutocompleteListAction,
  };
};

export default useGetPrimaryAccountsAutocompleteList;
